import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api, formatApiError, useAuth } from '@/App';
import { Plus, MagnifyingGlass, User, Trash, PencilSimple, Copy, Key, CheckCircle, XCircle } from '@phosphor-icons/react';

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  staff: 'bg-blue-100 text-blue-700',
  client: 'bg-gray-100 text-gray-700',
};

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('staff'); // 'staff' = admin+staff, 'clients' = client only
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetPwdTarget, setResetPwdTarget] = useState(null);   // user we're resetting
  const [resetPwdInput, setResetPwdInput] = useState('');       // custom pwd (empty = auto)
  const [resetNotify, setResetNotify] = useState(true);         // email the user
  const [resetResult, setResetResult] = useState(null);         // {email,new_password,email_status,email_error}
  const [busy, setBusy] = useState(false);
  const [tempCred, setTempCred] = useState(null); // { email, password }
  const [counts, setCounts] = useState({ staff: 0, clients: 0 });

  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'staff', password: '' });

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const { data } = await api.get(`/users?${params.toString()}`);
      const all = data || [];
      // Group filter (client-side so we keep one fetch + see counts both ways)
      const filtered = group === 'clients'
        ? all.filter((u) => u.role === 'client')
        : all.filter((u) => u.role === 'admin' || u.role === 'staff');
      setUsers(filtered);
      setCounts({
        staff: all.filter((u) => u.role === 'admin' || u.role === 'staff').length,
        clients: all.filter((u) => u.role === 'client').length,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...newUser };
      if (!payload.password) delete payload.password;
      const { data } = await api.post('/users', payload);
      toast.success('User created — welcome email sent');
      if (data.temp_password) setTempCred({ email: data.email, password: data.temp_password });
      setCreateOpen(false);
      setNewUser({ email: '', name: '', role: 'staff', password: '' });
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: editTarget.name,
        role: editTarget.role,
        is_active: editTarget.is_active,
      };
      if (editTarget._new_password) payload.password = editTarget._new_password;
      await api.put(`/users/${editTarget.user_id}`, payload);
      toast.success('User updated');
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    if (!resetPwdTarget) return;
    setBusy(true);
    setResetResult(null);
    try {
      const { data } = await api.post(`/users/${resetPwdTarget.user_id}/reset-password`, {
        new_password: resetPwdInput.trim() || null,
        notify_user: resetNotify,
      });
      setResetResult(data);
      toast.success(
        data.email_status === 'sent'
          ? `Password reset. Email sent to ${data.email}.`
          : resetNotify
          ? `Password reset — but email delivery failed. Share the password below.`
          : `Password reset. Share the password below with the user.`
      );
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const closeResetDialog = () => {
    setResetPwdTarget(null);
    setResetPwdInput('');
    setResetNotify(true);
    setResetResult(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/users/${deleteTarget.user_id}`);
      toast.success('User deleted');
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F]">User Management</h1>
            <p className="text-sm text-gray-500 mt-1">Manage admin, staff and client accounts.</p>
          </div>
          <Button
            onClick={() => { setNewUser({ email: '', name: '', role: group === 'clients' ? 'client' : 'staff', password: '' }); setCreateOpen(true); }}
            className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add {group === 'clients' ? 'Client' : 'Staff'}
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Group tabs */}
            <div className="flex border-b border-gray-200" data-testid="user-group-tabs">
              <button
                type="button"
                onClick={() => setGroup('staff')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${group === 'staff' ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-staff"
              >
                Staff & Admins <span className="ml-1 text-xs text-gray-400">({counts.staff})</span>
              </button>
              <button
                type="button"
                onClick={() => setGroup('clients')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${group === 'clients' ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid="tab-clients"
              >
                Clients <span className="ml-1 text-xs text-gray-400">({counts.clients})</span>
              </button>
            </div>

            <div className="relative">
              <MagnifyingGlass className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={group === 'clients' ? 'Search clients by name or email...' : 'Search staff by name or email...'}
                className="pl-10 rounded-none"
                data-testid="user-search"
              />
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="empty-users">
                <User className="w-10 h-10 mx-auto mb-2 text-gray-300" /> No {group === 'clients' ? 'clients' : 'staff or admins'} found
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {users.map((u) => (
                  <div key={u.user_id} className="py-3 flex items-center justify-between gap-4" data-testid={`user-row-${u.user_id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-full bg-[#0055FF] text-white text-sm font-semibold flex items-center justify-center shrink-0">
                        {(u.name || u.email)[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0A192F] truncate">{u.name || '—'}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={ROLE_COLORS[u.role] || 'bg-gray-100'}>{u.role}</Badge>
                      {u.is_active === false && <Badge variant="outline" className="text-amber-600 border-amber-300">Inactive</Badge>}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditTarget({ ...u, _new_password: '' })}
                        data-testid={`edit-user-${u.user_id}`}
                        aria-label="Edit user"
                      >
                        <PencilSimple className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[#0055FF] hover:text-[#0044CC]"
                        onClick={() => { setResetPwdTarget(u); setResetPwdInput(''); setResetNotify(true); setResetResult(null); }}
                        data-testid={`reset-pwd-${u.user_id}`}
                        aria-label="Reset password"
                        title="Reset password"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {u.user_id !== currentUser?.user_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => setDeleteTarget(u)}
                          data-testid={`delete-user-${u.user_id}`}
                          aria-label="Delete user"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => !busy && setCreateOpen(o)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Add {newUser.role === 'client' ? 'Client' : 'Staff Member'}</DialogTitle>
            <DialogDescription>The user will receive a welcome email with their temporary password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-semibold">Full Name *</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                     required className="rounded-none mt-1" data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Email *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                     required className="rounded-none mt-1" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Role *</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full access</SelectItem>
                  <SelectItem value="staff">Staff — manage cases</SelectItem>
                  <SelectItem value="client">Client — portal only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Password (optional)</Label>
              <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                     placeholder="Leave empty to auto-generate" className="rounded-none mt-1 font-mono" data-testid="new-user-password" />
              <p className="text-xs text-gray-500 mt-1">If empty, a secure password will be generated and emailed.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={busy} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-create-user">
                {busy ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && !busy && setEditTarget(null)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update profile, role and access for this user.</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={handleSaveEdit} className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-semibold">Email</Label>
                <Input value={editTarget.email} disabled className="rounded-none mt-1 bg-gray-50 text-gray-500" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Full Name *</Label>
                <Input value={editTarget.name || ''} onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })}
                       required className="rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Role *</Label>
                <Select value={editTarget.role} onValueChange={(v) => setEditTarget({ ...editTarget, role: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editTarget.is_active !== false}
                       onChange={(e) => setEditTarget({ ...editTarget, is_active: e.target.checked })}
                       className="w-4 h-4 accent-[#0055FF]" />
                <span className="text-sm">Account is active</span>
              </label>
              <div>
                <Label className="text-sm font-semibold">Reset Password (optional)</Label>
                <Input type="text" value={editTarget._new_password || ''}
                       onChange={(e) => setEditTarget({ ...editTarget, _new_password: e.target.value })}
                       placeholder="Leave empty to keep current" className="rounded-none mt-1 font-mono" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditTarget(null)} disabled={busy} className="rounded-none">Cancel</Button>
                <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-edit-user">
                  {busy ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name || deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              The user account will be permanently removed. Their cases and history remain intact. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password dialog */}
      <Dialog open={!!resetPwdTarget} onOpenChange={(o) => !o && !busy && closeResetDialog()}>
        <DialogContent className="sm:max-w-[500px]" data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-[#0055FF]" weight="fill" />
              Reset password
            </DialogTitle>
            <DialogDescription>
              {resetPwdTarget && (
                <>Set a new password for <strong>{resetPwdTarget.name || resetPwdTarget.email}</strong> ({resetPwdTarget.email}).</>
              )}
            </DialogDescription>
          </DialogHeader>

          {!resetResult ? (
            <form onSubmit={handleResetPassword} className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-semibold">New password (optional)</Label>
                <Input
                  type="text"
                  value={resetPwdInput}
                  onChange={(e) => setResetPwdInput(e.target.value)}
                  placeholder="Leave empty to auto-generate a secure password"
                  minLength={8}
                  className="rounded-none mt-1 font-mono"
                  data-testid="reset-pwd-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-generated passwords are 12 characters, letters + digits, no ambiguous characters.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetNotify}
                  onChange={(e) => setResetNotify(e.target.checked)}
                  className="w-4 h-4 accent-[#0055FF]"
                  data-testid="reset-notify-checkbox"
                />
                <span className="text-sm">Email the new password to the user</span>
              </label>
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <strong>Heads-up:</strong> The user&apos;s current password will be invalidated immediately.
                Any active sessions remain until they log out.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeResetDialog} disabled={busy} className="rounded-none">Cancel</Button>
                <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-reset-pwd">
                  {busy ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3 mt-2" data-testid="reset-pwd-result">
              <div className="p-3 bg-green-50 border border-green-200 font-mono text-sm">
                <div className="mb-1"><strong>Email:</strong> {resetResult.email}</div>
                <div><strong>New Password:</strong> {resetResult.new_password}</div>
              </div>
              <div
                className={`flex items-start gap-2 p-3 text-xs ${
                  resetResult.email_status === 'sent'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : resetResult.email_status === 'skipped'
                    ? 'bg-gray-50 border border-gray-200 text-gray-700'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {resetResult.email_status === 'sent' ? (
                  <><CheckCircle className="w-4 h-4 shrink-0 mt-0.5" weight="fill" />
                    <div>Password reset email delivered to <strong>{resetResult.email}</strong>. Share this
                    password only if the email doesn&apos;t arrive.</div></>
                ) : resetResult.email_status === 'skipped' ? (
                  <><Copy className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>Email delivery was not requested. Share the password via your preferred secure channel.</div></>
                ) : (
                  <><XCircle className="w-4 h-4 shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <div>Email delivery failed. Share this password with the user manually.</div>
                      {resetResult.email_error && (
                        <div className="mt-1 font-mono opacity-80 break-all">{resetResult.email_error}</div>
                      )}
                    </div></>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(`${resetResult.email} / ${resetResult.new_password}`); toast.success('Copied to clipboard'); }}
                  variant="outline"
                  className="rounded-none flex-1"
                  data-testid="copy-reset-pwd"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy credentials
                </Button>
                <Button
                  type="button"
                  onClick={closeResetDialog}
                  className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none flex-1"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Temp credential modal */}
      <Dialog open={!!tempCred} onOpenChange={(o) => !o && setTempCred(null)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="temp-cred-dialog">
          <DialogHeader>
            <DialogTitle>Temporary Password Generated</DialogTitle>
            <DialogDescription>
              The welcome email has been sent. Copy this in case the email is delayed or filtered.
            </DialogDescription>
          </DialogHeader>
          {tempCred && (
            <div className="space-y-3 mt-2">
              <div className="p-3 bg-amber-50 border border-amber-200 font-mono text-sm">
                <div><strong>Email:</strong> {tempCred.email}</div>
                <div><strong>Password:</strong> {tempCred.password}</div>
              </div>
              <Button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(`${tempCred.email} / ${tempCred.password}`); toast.success('Copied'); }}
                variant="outline"
                className="rounded-none w-full"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy to clipboard
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
